import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Bell, X, Newspaper, TrendingUp } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

const SEEN_KEY = "sfx_news_seen_at";

export const NewsBell = () => {
  const [items, setItems] = useState([]);
  const [news, setNews] = useState([]);
  const [events, setEvents] = useState([]);
  const [seenAt, setSeenAt] = useState(() => localStorage.getItem(SEEN_KEY) || "");

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(Array.isArray(data) ? data : []);
    } catch { /* ignore */ }
    try {
      const { data } = await api.get("/news/market");
      setNews(Array.isArray(data) ? data : []);
    } catch { /* ignore */ }
    try {
      const { data } = await api.get("/cpi/status");
      setEvents(data?.upcoming || []);
    } catch { /* ignore */ }
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  const unread = items.filter((n) => !seenAt || (n.created_at || "") > seenAt).length;

  const markSeen = () => {
    const now = new Date().toISOString();
    localStorage.setItem(SEEN_KEY, now);
    setSeenAt(now);
  };

  return (
    <Dialog onOpenChange={(o) => { if (o) markSeen(); }}>
      <DialogTrigger asChild>
        <button data-testid="news-bell-button" aria-label="News & notifications"
          className="relative h-8 w-8 rounded-full border border-white/30 flex items-center justify-center text-white/80 hover:text-white hover:border-white transition-colors">
          <Bell className="h-4 w-4" />
          {unread > 0 && (
            <span data-testid="news-unread-dot"
              className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-[#ff3b30] text-white text-[10px] font-bold flex items-center justify-center">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </DialogTrigger>
      <DialogContent data-testid="news-fullscreen"
        className="bg-[#060c18] text-white border-0 p-0 gap-0 max-w-full w-screen h-[100dvh] left-0 top-0 translate-x-0 translate-y-0 rounded-none sm:rounded-none flex flex-col">
        <DialogHeader className="px-5 pt-6 pb-4 border-b border-[#14233d] shrink-0">
          <DialogTitle className="flex items-center gap-2 font-heading text-lg">
            <Bell className="h-5 w-5" style={{ color: "var(--sfx-accent)" }} /> News &amp; Updates
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-3" data-testid="news-list">
          {events.length > 0 && (
            <div data-testid="upcoming-events-list">
              <div className="font-mono-term text-[10px] uppercase tracking-widest text-[#5f7ba6] mb-2 flex items-center gap-1.5">
                <TrendingUp className="h-3 w-3 text-[#ff5b5b]" /> Upcoming High-Impact Events
              </div>
              <div className="space-y-2 mb-2">
                {events.map((ev) => (
                  <div key={ev.release_time_utc} data-testid={`upcoming-event-${ev.type}`}
                    className="flex items-center gap-3 border border-[#14233d] bg-[#0A1120] rounded-xl px-3 py-2.5">
                    <span className="px-2 py-0.5 rounded-md text-[10px] font-bold font-mono-term" style={{ backgroundColor: "var(--sfx-accent-soft)", color: "var(--sfx-accent)" }}>{ev.type}</span>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold truncate">{ev.label}</div>
                      <div className="font-mono-term text-[10px] text-[#5f7ba6]">{ev.scheduled_et}</div>
                    </div>
                    <TrendingUp className="h-3.5 w-3.5 text-[#ff5b5b]" />
                  </div>
                ))}
              </div>
            </div>
          )}
          {items.length === 0 ? (
            <p className="text-sm text-[#5f7ba6] py-6 text-center">No platform updates yet.</p>
          ) : (
            items.map((n) => (
              <div key={n.id} data-testid={`news-item-${n.id}`} className="border border-[#14233d] bg-[#0A1120] rounded-xl p-4">
                <div className="font-heading font-semibold text-base">{n.title}</div>
                <p className="text-sm text-[#8fb4e6] mt-1.5 leading-relaxed">{n.message}</p>
                <div className="font-mono-term text-[10px] uppercase tracking-widest text-[#5f7ba6] mt-2.5">
                  {n.created_at ? new Date(n.created_at).toLocaleString() : ""}
                </div>
              </div>
            ))
          )}

          {news.length > 0 && (
            <div className="pt-4" data-testid="market-news-list">
              <div className="font-mono-term text-[10px] uppercase tracking-widest text-[#5f7ba6] mb-2 flex items-center gap-1.5">
                <Newspaper className="h-3 w-3" /> Live Market News
              </div>
              <div className="space-y-3">
                {news.map((n) => (
                  <a key={n.id} href={n.url} target="_blank" rel="noreferrer"
                    data-testid={`market-news-${n.id}`}
                    className="flex gap-3 border border-[#14233d] bg-[#0A1120] rounded-xl p-3 hover:border-[#1C8CFF]/50 transition-colors">
                    {n.image ? (
                      <img src={n.image} alt="" className="h-12 w-12 rounded-lg object-cover shrink-0" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                    ) : null}
                    <div className="min-w-0">
                      <div className="font-semibold text-sm leading-snug line-clamp-2">{n.headline}</div>
                      <div className="font-mono-term text-[10px] uppercase tracking-widest text-[#5f7ba6] mt-1.5">
                        {n.source}{n.datetime ? " · " + new Date(n.datetime * 1000).toLocaleDateString() : ""}
                      </div>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
